import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from "lucide-react";

const EmailVerified = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(5);
  const hasStoredToken = useRef(false);
  const hasRedirected = useRef(false);

  useEffect(() => {
    const success = searchParams.get("success");
    const emailParam = searchParams.get("email");
    const token = searchParams.get("token");
    const message = searchParams.get("message");
    const alreadyVerified = searchParams.get("already_verified");

    console.log("📧 Email verification page loaded:", { 
      success, 
      emailParam, 
      token: token ? "present" : "missing",
      message,
      alreadyVerified 
    });

    if (!success || !emailParam || !token) {
      console.error("❌ Missing required parameters");
      setError(message || "Invalid verification link. Missing required information.");
      setLoading(false);
      
      setTimeout(() => {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          navigate("/auth?error=invalid_verification_link", { replace: true });
        }
      }, 5000);
      return;
    }

    if (success === "true") {
      setEmail(decodeURIComponent(emailParam));
      setLoading(false);

      if (!hasStoredToken.current) {
        try {
          localStorage.setItem("token", token);
          hasStoredToken.current = true;
          console.log("✅ Token stored successfully");
        } catch (err) {
          console.error("❌ Failed to store token:", err);
          setError("Failed to save authentication. Please log in manually.");
          return;
        }
      }

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            if (!hasRedirected.current) {
              hasRedirected.current = true;
              console.log("⏰ Auto-redirecting to dashboard...");
              window.location.href = "/dashboard";
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    } else {
      console.error("❌ Verification failed");
      setError(message || "Email verification failed. Please try again.");
      setLoading(false);
      
      setTimeout(() => {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          navigate("/auth?error=verification_failed", { replace: true });
        }
      }, 5000);
    }
  }, [searchParams, navigate]);

  const handleManualRedirect = () => {
    if (!hasRedirected.current) {
      hasRedirected.current = true;
      console.log("👆 Manual redirect to dashboard");
      window.location.href = "/dashboard";
    }
  };

  const cardClass = "w-full max-w-[440px] sm:max-w-[480px] text-center p-6 sm:p-8 rounded-xl shadow-lg relative z-10 animate-slide-up bg-white/10 backdrop-blur-xl border border-white/20";
  
  const containerClass = "min-h-screen w-full flex items-center justify-center p-3";

  return (
    <>
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bounceIn { 0% { opacity: 0; transform: scale(0.3); } 50% { opacity: 1; transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { transform: scale(1); } }
        @keyframes shakeError { 0%, 100% { transform: translateX(0); } 10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); } 20%, 40%, 60%, 80% { transform: translateX(10px); } }
        @keyframes rotate { 0% { transform: rotate(0deg); } 25% { transform: rotate(-10deg); } 75% { transform: rotate(10deg); } 100% { transform: rotate(0deg); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-slide-up { animation: slideUp 0.6s ease-out; }
        .animate-bounce-in { animation: bounceIn 0.8s ease-out; }
        .animate-shake { animation: shakeError 0.5s ease-in-out; }
        .animate-rotate { animation: rotate 1s ease-in-out; }
        .animate-spin-slow { animation: spin 1s linear infinite; }
      `}</style>
      
      {loading ? (
        <div className={`${containerClass} bg-gradient-to-b from-green-500/10 to-transparent`}>
          <div className={`${cardClass} py-12 px-8`}>
            <div className="flex items-center justify-center mb-6">
              <Loader2 className="w-16 h-16 sm:w-18 sm:h-18 text-primary animate-spin-slow" />
            </div>
            <p className="text-base sm:text-lg text-secondary font-medium">Verifying your email...</p>
          </div>
        </div>
      ) : error ? (
        <div className={`${containerClass} bg-gradient-to-b from-red-500/10 to-transparent`}>
          <div className={`${cardClass} border-red-500/30`}>
            <div className="flex items-center justify-center mb-6">
              <AlertCircle className="w-16 h-16 sm:w-18 sm:h-18 text-red-500 animate-shake" />
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">Verification Failed</h1>
            
            <p className="text-base sm:text-lg text-secondary mb-6 leading-relaxed">{error}</p>
            
            <button
              onClick={() => navigate("/auth", { replace: true })}
              className="w-full py-3.5 sm:py-4 bg-gradient-to-r from-red-500 to-red-600 text-white border-none rounded-lg text-base font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-500/40 active:scale-[0.98]"
            >
              <span>Return to Login</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        <div className={`${containerClass} bg-gradient-to-b from-green-500/10 to-transparent`}>
          <div className={`${cardClass} border-green-500/30`}>
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-500/20 rounded-full flex items-center justify-center border-2 border-green-400 animate-bounce-in">
                <CheckCircle className="w-12 h-12 sm:w-14 sm:h-14 text-green-400" />
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight">
              Email Verified! <span className="inline-block animate-rotate">🎉</span>
            </h1>

            <div className="my-6 p-5 bg-white/10 border border-white/20 rounded-lg">
              <p className="text-sm text-secondary mb-2 font-medium">Your email has been successfully verified:</p>
              <p className="text-lg font-semibold text-white break-all tracking-tight">{email}</p>
            </div>

            <div className="my-6 p-5 bg-green-500/20 border border-green-500/40 rounded-lg backdrop-blur-md">
              <p className="text-base sm:text-lg font-semibold text-green-400 mb-3 tracking-tight">
                Redirecting in <span className="inline-block min-w-[1.5ch] text-center text-2xl sm:text-3xl font-bold">{countdown}</span> second{countdown !== 1 ? 's' : ''}...
              </p>
              <div className="w-full h-2 bg-green-500/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-400 rounded-full transition-all duration-1000 ease-linear shadow-[0_0_10px_rgba(52,199,89,0.5)]"
                  style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                />
              </div>
            </div>

            <button
              onClick={handleManualRedirect}
              disabled={hasRedirected.current}
              className="w-full py-3.5 sm:py-4 bg-gradient-to-r from-primary to-primary-dark text-white border-none rounded-lg text-base font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/40 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-0"
            >
              <span>{hasRedirected.current ? "Redirecting..." : "Go to Dashboard Now"}</span>
              <ArrowRight className="w-5 h-5" />
            </button>

            <p className="mt-6 text-sm text-muted font-medium">Welcome to WaveBudget! 🌊</p>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailVerified;